import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const CaseStudies = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Case Studies", url: "/case-studies" }
  ];
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "AI Data Operations Case Studies",
    "description": "Real-world success stories from medical AI, sports analytics, and forestry management projects powered by Orboroi's data services.",
    "url": `${siteUrl}/case-studies`
  };
  
  const caseStudies = [
    {
      id: 'slicevault',
      slug: 'case-study-slicevault',
      company: 'SliceVault',
      industry: 'Medical AI',
      lifecycleStage: 'Data Preparation & Model Evaluation',
      excerpt: 'Achieved 99%+ annotation precision on 10,000+ medical images for CT, MRI, and X-ray organ segmentation, accelerating clinical AI deployment.',
      image: 'https://images.pexels.com/photos/7089298/pexels-photo-7089298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      bgColor: 'from-purple-900 to-indigo-900'
    },
    {
      id: 'spiideo',
      slug: 'case-study-spiideo',
      company: 'Spiideo AB',
      industry: 'Sports AI',
      lifecycleStage: 'End-to-End AI Operations',
      excerpt: 'Annotated 30,000+ sports videos with player and object tracking, delivering 99%+ accuracy through a year-long embedded collaboration.',
      image: 'https://images.unsplash.com/photo-1613758403772-268da019247a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB2aWRlbyUyMHRyYWNraW5nJTIwcGxheWVyJTIwZGV0ZWN0aW9uJTIwb2JqZWN0JTIwdHJhY2tpbmclMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc0Mjk4NTkxfDA&ixlib=rb-4.1.0&q=85',
      bgColor: 'from-blue-900 to-cyan-900'
    },
    {
      id: 'katam',
      slug: 'case-study-katam',
      company: 'Katam Technologies',
      industry: 'Forestry AI',
      lifecycleStage: 'Data Preparation & Custom Solutions',
      excerpt: 'Delivered 99%+ accuracy on 10,000+ forestry images with bounding box annotations for tree species detection and optimization.',
      image: 'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxmb3Jlc3QlMjB0cmVlcyUyMGRldGVjdGlvbiUyMGNvbXB1dGVyJTIwdmlzaW9ufGVufDB8fHx8MTc3NDI5ODU5NHww&ixlib=rb-4.1.0&q=85',
      bgColor: 'from-green-900 to-emerald-900'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Case Studies - Client Success Stories"
        description="Real-world AI success stories: medical imaging, sports analytics, forestry management. See how Orboroi delivers 99%+ accuracy for production AI."
        canonical="/case-studies"
        type="website"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Client <span className="text-[#379F7F]">Success Stories</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              See how leading organizations achieve production-ready AI with Orboroi's data operations and quality assurance services.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {caseStudies.map((study) => (
              <Link 
                key={study.id}
                to={`/${study.slug}`}
                className="block h-full"
              >
                <Card 
                  className="bg-gray-900 border-gray-800 hover:border-[#379F7F] transition-all duration-300 overflow-hidden group cursor-pointer h-full flex flex-col"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${study.bgColor} opacity-80`}></div>
                    <LazyImage 
                      src={study.image} 
                      alt={study.company}
                      className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-300"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <h3 className="text-2xl font-bold text-white group-hover:text-[#379F7F] transition-colors duration-300">{study.company}</h3>
                    </div>
                  </div>

                  <CardContent className="p-6 flex-grow flex flex-col justify-between">
                    <div>
                      <div className="mb-4">
                        <span className="text-sm text-[#379F7F] font-semibold">{study.industry}</span>
                        <p className="text-xs text-gray-500 mt-1">{study.lifecycleStage}</p>
                      </div>

                      <p className="text-gray-300 mb-6 leading-relaxed">
                        {study.excerpt}
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-[#379F7F] text-sm font-medium">
                      <span>Read Full Case Study</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Build Your Success Story?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how Orboroi can help you achieve production-ready AI with data certainty.
            </p>
            <Link to="/contact">
              <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6">
                Talk to a Data Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
